import { Controller } from "@hotwired/stimulus";
import { GroupProfileDomHelpers } from "../services/GroupProfileDomHelpers";

/**
 * Quantity Controller for Product Group Profile Step
 *
 * Responsibilities:
 * - Handle quantity selection dropdowns
 * - Manage height inputs (dynamically create/update based on quantity)
 * - Track group quantities in memory
 * - Update group summaries when quantities change
 * - Sync quantity data to form fields
 * - Handle existing selections on page load
 *
 * Events listened:
 * - change on quantity select dropdowns
 * - group-profile:load-existing-selections - Load existing quantity data
 * - group-profile:sync-to-form - Sync quantities to hidden form fields
 *
 * Events dispatched:
 * - group-profile:quantity-changed - When a quantity changes
 * - group-profile:quantities-synced - When quantities are synced to form
 */
export default class ProductGroupProfileQuantityController extends Controller {
  static values = {
    existingSelections: {
      type: Object,
      default: {},
    },
    quantitySelectionText: {
      type: String,
      default: "%{selected} of %{total} positions selected",
    },
  };

  static targets = ["heightContainer"];

  connect() {
    this.groupQuantities = {};

    this.element.addEventListener(
      "group-profile:load-existing-selections",
      this.handleLoadExistingSelections.bind(this)
    );
    this.element.addEventListener(
      "group-profile:sync-to-form",
      this.handleSyncToForm.bind(this)
    );
    this.element.addEventListener(
      "group-profile:get-quantities",
      this.handleGetQuantities.bind(this)
    );

    this.initializeHeightInputs();
  }

  handleLoadExistingSelections(event) {
    const { selections } = event.detail;

    if (!selections) return;

    Object.entries(selections).forEach(([groupId, selectionData]) => {
      if (selectionData.type === "quantity") {
        this.groupQuantities[groupId] = selectionData.quantities || {};

        Object.entries(selectionData.quantities || {}).forEach(
          ([skuId, quantity]) => {
            const quantitySelect = this.element.querySelector(
              `select[data-sku-id="${skuId}"][data-group-id="${groupId}"]`
            );
            if (quantitySelect) {
              quantitySelect.value = quantity;
              const changeEvent = new Event("change", { bubbles: true });
              quantitySelect.dispatchEvent(changeEvent);
            }
          }
        );

        this.updateQuantityGroupSummary(groupId);
      }
    });
  }

  handleSyncToForm() {
    this.syncQuantitiesToFormFields();

    GroupProfileDomHelpers.dispatchEvent(
      this.element,
      "group-profile:quantities-synced",
      {
        quantities: this.groupQuantities,
      }
    );
  }

  handleGetQuantities(event) {
    // eslint-disable-next-line no-param-reassign
    event.detail.quantities = this.groupQuantities || {};
  }

  updateQuantity(event) {
    const { skuId } = event.target.dataset;
    const { groupId } = event.target.dataset;
    const quantity = parseInt(event.target.value, 10) || 0;

    this.updateQuantityForGroup(groupId, skuId, quantity);
    this.updateHeightInputsForResource(skuId, quantity);
  }

  updateQuantityForGroup(groupId, skuId, quantity) {
    if (!this.groupQuantities) {
      this.groupQuantities = {};
    }
    if (!this.groupQuantities[groupId]) {
      this.groupQuantities[groupId] = {};
    }

    if (quantity > 0) {
      this.groupQuantities[groupId][skuId] = quantity;
    } else {
      delete this.groupQuantities[groupId][skuId];
    }

    this.syncQuantitiesToFormFields();
    this.updateQuantityGroupSummary(groupId);

    GroupProfileDomHelpers.dispatchEvent(
      this.element,
      "group-profile:quantity-changed",
      {
        groupId,
        skuId,
        quantity,
        allQuantities: this.groupQuantities,
      }
    );
  }

  updateQuantityGroupSummary(groupId) {
    const summaryLabels = this.element.querySelectorAll(
      `.product-group-profile-summary-label[data-group-id="${groupId}"]`
    );
    const statusMarkers = this.element.querySelectorAll(
      `.product-group-profile-status-marker[data-group-id="${groupId}"]`
    );

    if (summaryLabels.length > 0) {
      const requiredCount =
        parseInt(summaryLabels[0].dataset.requiredCount, 10) || 0;
      const selectedCount = this.getTotalQuantityForGroup(groupId);

      const template = this.quantitySelectionTextValue;
      const displayText = template
        .replace(/%\{selected\}/g, selectedCount)
        .replace(/%\{total\}/g, requiredCount)
        .replace(/\{selected\}/g, selectedCount)
        .replace(/\{total\}/g, requiredCount);

      summaryLabels.forEach((summaryLabel) => {
        // eslint-disable-next-line no-param-reassign
        summaryLabel.dataset.selectedCount = selectedCount;
        // eslint-disable-next-line no-param-reassign
        summaryLabel.textContent = displayText;
      });

      if (statusMarkers.length > 0) {
        const shouldShow = selectedCount >= requiredCount && requiredCount > 0;
        statusMarkers.forEach((statusMarker) => {
          if (shouldShow) {
            statusMarker.classList.remove("hidden");
          } else {
            statusMarker.classList.add("hidden");
          }
        });
      }
    }
  }

  getTotalQuantityForGroup(groupId) {
    if (!this.groupQuantities || !this.groupQuantities[groupId]) {
      return 0;
    }

    return Object.values(this.groupQuantities[groupId]).reduce(
      (sum, qty) => sum + qty,
      0
    );
  }

  syncQuantitiesToFormFields() {
    const form = this.element.closest("form");
    if (!form || !this.groupQuantities) {
      return;
    }

    const existingFields = form.querySelectorAll(
      'input[name^="booking[resource_sku_"]'
    );
    existingFields.forEach((field) => {
      if (field.name.match(/booking\[resource_sku_\d+_\d+\]/)) {
        field.remove();
      }
    });

    Object.entries(this.groupQuantities).forEach(([groupId, skuQuantities]) => {
      let positionIndex = 0;

      Object.entries(skuQuantities).forEach(([skuId, quantity]) => {
        for (let i = 0; i < quantity; i += 1) {
          const fieldName = `booking[resource_sku_${groupId}_${positionIndex}]`;
          const hiddenField = document.createElement("input");
          hiddenField.type = "hidden";
          hiddenField.name = fieldName;
          hiddenField.value = skuId;
          hiddenField.dataset.quantityField = "true";

          form.appendChild(hiddenField);

          positionIndex += 1;
        }
      });
    });
  }

  initializeHeightInputs() {
    const quantitySelects = this.element.querySelectorAll(
      'select[data-action*="updateGroupQuantity"]'
    );

    quantitySelects.forEach((select) => {
      const { skuId } = select.dataset;
      const quantity = parseInt(select.value, 10) || 0;
      if (quantity > 0) {
        this.updateHeightInputsForResource(skuId, quantity);
      }
    });
  }

  updateHeightInputsForResource(skuId, quantity) {
    const heightContainer = this.element.querySelector(
      `[data-resource-sku-id="${skuId}"]`
    );

    if (!heightContainer) return;

    const { attributeName } = heightContainer.dataset;

    const oldGrid = heightContainer.querySelector(".grid");
    if (oldGrid) {
      oldGrid.remove();
    }

    while (heightContainer.firstChild) {
      heightContainer.removeChild(heightContainer.firstChild);
    }

    const parentContainer = heightContainer.closest(".mt-3");

    if (quantity === 0) {
      if (parentContainer) {
        parentContainer.classList.remove("pt-3", "border-t", "border-gray-200");
      }
      return;
    }

    if (parentContainer) {
      parentContainer.classList.add("pt-3", "border-t", "border-gray-200");
    }

    const gridContainer = document.createElement("div");
    gridContainer.className =
      "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mt-2";

    let initialHeights = [];
    try {
      if (heightContainer.dataset.initialHeights) {
        initialHeights = JSON.parse(heightContainer.dataset.initialHeights);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Failed to parse initial heights", e);
    }

    for (let i = 0; i < quantity; i += 1) {
      const fieldName = `booking[rental_bike_heights][${skuId}][${i}]`;
      const initialValue = initialHeights[i] || "";
      let validInitialValue = "";

      if (initialValue && /^\d+$/.test(initialValue)) {
        const intVal = parseInt(initialValue, 10);
        if (intVal >= 100 && intVal <= 220) {
          validInitialValue = intVal.toString();
        }
      }

      const inputGroup = document.createElement("div");
      inputGroup.className = "flex flex-col gap-1";

      const label = document.createElement("label");
      label.setAttribute("for", fieldName);
      label.className = "text-xs text-gray-500 font-medium";
      label.textContent = attributeName || "Körpergröße";

      const select = document.createElement("select");
      select.name = fieldName;
      select.id = fieldName;
      select.className =
        "text-xs border border-gray-300 rounded px-3 py-1 w-full focus:outline-none focus:ring-2 focus:ring-blue-500";
      select.dataset.action =
        "change->product-group-profile-validation#validateAttributes";
      select.dataset.attributeRequired = "false";

      const defaultOption = document.createElement("option");
      defaultOption.value = "";
      defaultOption.textContent = "Körpergröße wählen";
      defaultOption.selected = !validInitialValue;
      select.appendChild(defaultOption);

      for (let height = 100; height <= 220; height += 1) {
        const option = document.createElement("option");
        option.value = height.toString();
        option.textContent = `${height} cm`;
        option.selected = validInitialValue === height.toString();
        select.appendChild(option);
      }

      const validationDiv = document.createElement("div");
      validationDiv.className = "text-xs text-red-600 mt-1 hidden";
      validationDiv.dataset.productGroupProfileValidationTarget =
        "attributeError";
      validationDiv.dataset.fieldName = fieldName;

      inputGroup.appendChild(label);
      inputGroup.appendChild(select);
      inputGroup.appendChild(validationDiv);
      gridContainer.appendChild(inputGroup);
    }

    heightContainer.appendChild(gridContainer);
  }
}
